import {
  Code2,
  Building2,
  Users,
  Smartphone,
  Layers,
  Rocket,
  ShoppingCart,
  Plug,
  PenTool,
  Cloud,
  Server,
  CloudCog,
  HardDrive,
  Globe,
  ShieldCheck,
  GitBranch,
  ArrowRightLeft,
  RefreshCcw,
  Database,
  BrainCircuit,
  Workflow,
  ShieldAlert,
  LifeBuoy,
  Lightbulb,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { SERVICES, type ServiceSlug } from '@vegaahi/shared';

interface ServiceContent {
  icon: LucideIcon;
  shortDescription: string;
  description: string;
  features: string[];
}

const SERVICE_CONTENT: Record<ServiceSlug, ServiceContent> = {
  'custom-website-development': {
    icon: Code2,
    shortDescription:
      'Bespoke, high-performance websites engineered around your brand and business goals.',
    description:
      'We design and build custom websites on modern frameworks — fast, accessible, and easy for your team to maintain — rather than templated builders that box you in as you grow.',
    features: [
      'Component-driven architecture',
      'Headless CMS integration',
      'Core Web Vitals optimized',
      'SEO-first markup',
    ],
  },
  'enterprise-erp-development': {
    icon: Building2,
    shortDescription:
      'Custom ERP systems that unify finance, inventory, procurement, and operations.',
    description:
      'We build enterprise resource planning platforms tailored to how your business actually runs, replacing spreadsheets and disconnected tools with a single source of truth.',
    features: [
      'Modular finance & inventory',
      'Role-based workflows',
      'Third-party integrations',
      'Real-time reporting',
    ],
  },
  'crm-solutions': {
    icon: Users,
    shortDescription: 'Customer relationship platforms that turn pipeline data into revenue.',
    description:
      'From lead capture to renewal, we build CRM systems that give sales and support teams one view of every customer relationship — configurable to your exact sales process.',
    features: [
      'Pipeline & lead scoring',
      'Automated follow-ups',
      'Sales analytics dashboards',
      'Email & calendar sync',
    ],
  },
  'mobile-app-development': {
    icon: Smartphone,
    shortDescription: 'Native-quality iOS and Android apps built for scale and App Store approval.',
    description:
      'We design and develop mobile applications with React Native and native tooling where it matters, so you ship one codebase without compromising on platform-native feel.',
    features: [
      'iOS & Android from one codebase',
      'Offline-first data sync',
      'Push notifications',
      'App Store & Play Store launch',
    ],
  },
  'progressive-web-apps': {
    icon: Layers,
    shortDescription:
      'Installable, offline-capable web apps that feel native without an app-store gate.',
    description:
      'PWAs give you app-like engagement — home-screen installs, push notifications, offline access — while shipping through the web, with one codebase and instant updates.',
    features: [
      'Offline caching strategy',
      'Installable on any device',
      'Push notifications',
      'App-shell architecture',
    ],
  },
  'saas-product-development': {
    icon: Rocket,
    shortDescription: 'End-to-end multi-tenant SaaS builds, from MVP to scaled production.',
    description:
      'We architect and build subscription software products — multi-tenancy, billing, usage metering, and admin tooling included — so you can focus on your product-market fit.',
    features: [
      'Multi-tenant architecture',
      'Subscription billing (Stripe)',
      'Usage-based metering',
      'Admin & customer portals',
    ],
  },
  'ecommerce-solutions': {
    icon: ShoppingCart,
    shortDescription: 'Storefronts and checkout experiences engineered to convert.',
    description:
      'From headless storefronts to full marketplace platforms, we build e-commerce experiences with fast checkout, inventory sync, and the analytics you need to grow AOV and retention.',
    features: [
      'Headless storefront options',
      'Payment gateway integration',
      'Inventory & order management',
      'Conversion-optimized checkout',
    ],
  },
  'api-development-integration': {
    icon: Plug,
    shortDescription:
      'Robust REST & GraphQL APIs, and integrations between the systems you already run.',
    description:
      'We design well-documented, versioned APIs and connect your existing tools — payment processors, CRMs, ERPs, and internal systems — into one coherent data flow.',
    features: [
      'REST & GraphQL API design',
      'OpenAPI documentation',
      'Webhook & event architecture',
      'Legacy system integration',
    ],
  },
  'ui-ux-design': {
    icon: PenTool,
    shortDescription: 'Research-driven product design that balances beauty with usability.',
    description:
      'Our design team maps user journeys, prototypes in Figma, and validates with real users before a line of production code is written — so builds start on solid ground.',
    features: [
      'User research & journey mapping',
      'Interactive prototyping',
      'Design systems',
      'Accessibility (WCAG 2.2 AA)',
    ],
  },
  'cloud-solutions': {
    icon: Cloud,
    shortDescription: 'Cloud architecture and migration across AWS, Azure, and GCP.',
    description:
      'We design cloud infrastructure for reliability and cost efficiency — right-sized from day one, with a clear path to scale as your traffic and team grow.',
    features: [
      'Multi-cloud architecture',
      'Infrastructure as Code',
      'Auto-scaling & load balancing',
      'Cost optimization audits',
    ],
  },
  'vps-hosting': {
    icon: Server,
    shortDescription: 'Managed virtual private servers, tuned and monitored for you.',
    description:
      'Skip the DevOps overhead — we provision, harden, and monitor VPS environments so your applications stay fast and available without you managing infrastructure directly.',
    features: [
      'Managed provisioning',
      'Automated backups',
      '24/7 uptime monitoring',
      'Security hardening',
    ],
  },
  'cloud-hosting': {
    icon: CloudCog,
    shortDescription: 'Elastic, auto-scaling hosting for traffic that doesn’t sit still.',
    description:
      'Our cloud hosting scales with demand automatically, backed by CDN edge caching and health-checked failover, so traffic spikes never mean downtime.',
    features: [
      'Auto-scaling infrastructure',
      'Global CDN caching',
      'Zero-downtime deployments',
      'Real-time health monitoring',
    ],
  },
  'dedicated-servers': {
    icon: HardDrive,
    shortDescription: 'Fully dedicated hardware for workloads that need guaranteed resources.',
    description:
      'For compliance, performance, or compute-heavy workloads, we provision and manage dedicated server hardware with the same operational rigor as our cloud offerings.',
    features: [
      'Guaranteed dedicated resources',
      'Custom hardware specs',
      'Managed OS & patching',
      'Compliance-ready environments',
    ],
  },
  'domain-registration': {
    icon: Globe,
    shortDescription: 'Domain registration and DNS management, handled end-to-end.',
    description:
      'We register, configure, and manage your domain portfolio — DNS records, subdomain routing, and renewals — so your brand’s online identity never lapses.',
    features: [
      'Bulk domain registration',
      'DNS record management',
      'Auto-renewal monitoring',
      'Subdomain & email routing',
    ],
  },
  'ssl-certificate-management': {
    icon: ShieldCheck,
    shortDescription: 'Automated SSL/TLS provisioning, renewal, and monitoring.',
    description:
      'We set up and monitor SSL certificates across every domain and subdomain you run, with automated renewal so an expired certificate never takes your site offline.',
    features: [
      'Automated issuance & renewal',
      'Wildcard & multi-domain certs',
      'Expiry monitoring & alerts',
      'HTTPS enforcement & HSTS',
    ],
  },
  'devops-services': {
    icon: GitBranch,
    shortDescription: 'CI/CD pipelines and infrastructure automation that ship code safely, fast.',
    description:
      'We build deployment pipelines — automated testing, staging environments, and one-click rollbacks — so your team ships confidently, multiple times a day if needed.',
    features: [
      'CI/CD pipeline design',
      'Containerization (Docker/K8s)',
      'Infrastructure as Code',
      'Automated rollback strategies',
    ],
  },
  'application-migration': {
    icon: ArrowRightLeft,
    shortDescription: 'Move applications between platforms and clouds with minimal downtime.',
    description:
      'Whether you’re moving on-prem to cloud or switching providers, we plan and execute migrations with rollback strategies that protect uptime and data integrity.',
    features: [
      'Migration planning & audit',
      'Zero/low-downtime cutover',
      'Data integrity verification',
      'Post-migration monitoring',
    ],
  },
  'legacy-system-modernization': {
    icon: RefreshCcw,
    shortDescription: 'Bring aging systems onto modern, maintainable, secure foundations.',
    description:
      'We incrementally modernize legacy codebases and monoliths — reducing technical debt and security exposure — without the risk of a disruptive full rewrite.',
    features: [
      'Incremental modernization',
      'Monolith-to-service extraction',
      'Technical debt reduction',
      'Zero-disruption rollout',
    ],
  },
  'database-design-optimization': {
    icon: Database,
    shortDescription: 'Schema design and performance tuning for data that scales.',
    description:
      'We design normalized, indexed schemas and tune slow queries at scale — across SQL and NoSQL — so your data layer stays fast as volume grows.',
    features: [
      'Schema design & normalization',
      'Query performance tuning',
      'Replication & sharding strategy',
      'SQL & NoSQL expertise',
    ],
  },
  'ai-ml-solutions': {
    icon: BrainCircuit,
    shortDescription: 'Applied AI and machine learning features built into your product.',
    description:
      'From recommendation engines to document intelligence and LLM-powered assistants, we build production ML features grounded in your real data and use case.',
    features: [
      'LLM-powered assistants',
      'Predictive analytics models',
      'Document intelligence / OCR',
      'Recommendation engines',
    ],
  },
  'automation-solutions': {
    icon: Workflow,
    shortDescription: 'Workflow automation that removes manual, repetitive operational work.',
    description:
      'We identify and automate manual processes across your operations — approvals, data entry, reporting — freeing your team to focus on higher-value work.',
    features: [
      'Business process automation',
      'Cross-system workflow orchestration',
      'Scheduled & event-triggered jobs',
      'Human-in-the-loop approvals',
    ],
  },
  'cybersecurity-solutions': {
    icon: ShieldAlert,
    shortDescription: 'Security audits, hardening, and monitoring aligned to OWASP practices.',
    description:
      'We assess your applications and infrastructure against OWASP and industry best practices, then implement the hardening, monitoring, and response plans to back it up.',
    features: [
      'Security audits & pen-testing',
      'OWASP-aligned hardening',
      'Access control & RBAC design',
      'Incident response planning',
    ],
  },
  'software-maintenance-support': {
    icon: LifeBuoy,
    shortDescription: 'Ongoing maintenance, monitoring, and support SLAs for live systems.',
    description:
      'We keep production systems healthy after launch — dependency updates, monitoring, bug fixes, and support SLAs — so you’re never on your own post-handover.',
    features: [
      'Proactive monitoring & alerting',
      'Dependency & security patching',
      'Tiered support SLAs',
      'Regular health reports',
    ],
  },
  'technical-consulting': {
    icon: Lightbulb,
    shortDescription: 'Senior technical advisory for architecture, hiring, and platform decisions.',
    description:
      'When the decision is bigger than a single project — build vs. buy, platform selection, scaling strategy — our senior engineers advise alongside your leadership team.',
    features: [
      'Architecture reviews',
      'Technology selection',
      'Scaling & cost strategy',
      'Engineering process audits',
    ],
  },
  'digital-transformation': {
    icon: Sparkles,
    shortDescription: 'End-to-end modernization of how your business operates digitally.',
    description:
      'We partner across departments to digitize workflows, unify data, and modernize the systems your business runs on — a coordinated program, not a single project.',
    features: [
      'Cross-functional roadmapping',
      'Legacy-to-cloud strategy',
      'Change management support',
      'Measurable ROI milestones',
    ],
  },
};

export const SERVICES_DATA = SERVICES.map((service) => ({
  ...service,
  ...SERVICE_CONTENT[service.slug],
}));

export type ServiceData = (typeof SERVICES_DATA)[number];

export function getServiceBySlug(slug: string): ServiceData | undefined {
  return SERVICES_DATA.find((service) => service.slug === slug);
}

export function getServicesByCategory(category: string): ServiceData[] {
  return SERVICES_DATA.filter((service) => service.category === category);
}
